/**
 * Pure helpers that translate the attribute Configuration (coreAttributes,
 * keyed by fieldKey) into the enrichment form model, the load mapping, and the
 * save payload. No React, no I/O — unit-tested in enrichmentForm.test.js.
 */

const FORM_CATEGORIES = ['product_details', 'core_attributes', 'combination'];
const DEFAULT_CATEGORY_ORDER = 999;

function hasValues(cfg) {
  return (Array.isArray(cfg.values) && cfg.values.length > 0)
    || !!cfg.valuesByAxis || !!cfg.valuesByProductType;
}

export function widgetFor(cfg) {
  const c = cfg || {};
  if (c.dataType === 'numeric') return 'number';
  if (hasValues(c)) return c.multiSelect ? 'chips-multi' : 'chips-single';
  return 'text';
}

export function buildFormModel(coreAttributes) {
  const model = { product_details: [], core_attributes: [], combination: [] };
  Object.entries(coreAttributes || {}).forEach(([fieldKey, cfg]) => {
    if (cfg.enabled === false) return;
    if (!FORM_CATEGORIES.includes(cfg.category)) return;
    model[cfg.category].push({
      fieldKey,
      label: cfg.attributeName || cfg.label || fieldKey,
      dataType: cfg.dataType || 'string',
      dbColumn: cfg.dbColumn || fieldKey,
      multiSelect: !!cfg.multiSelect,
      widget: widgetFor(cfg),
      categoryOrder: cfg.categoryOrder ?? DEFAULT_CATEGORY_ORDER,
    });
  });
  FORM_CATEGORIES.forEach(c => model[c].sort((a, b) => a.categoryOrder - b.categoryOrder));
  return model;
}

export function requiredKeys(model, isRequiredFor, category) {
  return (model[category] || [])
    .filter(f => isRequiredFor(f.fieldKey))
    .map(f => f.fieldKey);
}

export function initEnrichmentForm(design, coreAttributes) {
  const fd = {};
  const meta = (design && design.metadata) || {};
  Object.entries(coreAttributes || {}).forEach(([fieldKey, cfg]) => {
    if (cfg.category === 'combination') return;
    let v;
    if ((cfg.dbColumn || '') === '_meta') {
      v = meta[fieldKey];
      if (Array.isArray(v)) v = v.join(', ');
    } else {
      v = design ? design[fieldKey] : undefined;
    }
    fd[fieldKey] = (v === undefined || v === null) ? '' : v;
  });
  return fd;
}

export function buildEnrichmentPayload(formData, coreAttributes) {
  const payload = {};
  const metadata = {};
  Object.entries(coreAttributes || {}).forEach(([fieldKey, cfg]) => {
    if (cfg.category === 'combination') return;
    const raw = formData ? formData[fieldKey] : undefined;
    const isMeta = (cfg.dbColumn || '') === '_meta';
    const isNumeric = cfg.dataType === 'numeric';

    if (isMeta) {
      if (cfg.multiSelect) {
        let arr;
        if (Array.isArray(raw)) arr = raw;
        else if (typeof raw === 'string' && raw.trim()) arr = raw.split(',');
        else arr = [];
        metadata[fieldKey] = arr.map(s => String(s).trim()).filter(Boolean);
        return;
      } else if (isNumeric) {
        metadata[fieldKey] = (raw === '' || raw === undefined || raw === null) ? null : Number(raw);
      } else {
        metadata[fieldKey] = raw ?? '';
      }
      return;
    }
    if (isNumeric) {
      payload[fieldKey] = (raw === '' || raw === undefined || raw === null) ? null : Number(raw);
      return;
    }
    payload[fieldKey] = raw ?? '';
  });
  payload.metadata = metadata;
  return payload;
}

export function buildComboFormData(design, comboFieldKeys) {
  const combos = {};
  ((design && design.combinations) || []).forEach(c => {
    const row = {};
    (comboFieldKeys || []).forEach(fk => { row[fk] = c[fk] || ''; });
    combos[c.combinationNumber] = row;
  });
  return combos;
}

/**
 * Pure detection helper. For each combination-tab attribute, classifies it as
 * "common" (every variant has the same value, or every variant is empty) or
 * "varying" (≥2 distinct non-empty values across variants, OR partial fill —
 * 1 has a value while others are empty). Multi-select values are compared
 * after sort-then-join so member order doesn't matter.
 *
 * The React hook `useDifferentiator` (in `CombinationTab.js`) is a thin
 * `useMemo` wrapper around this function.
 *
 * @param {Array<{fieldKey: string, multiSelect?: boolean}>} combinationFields  In display order.
 * @param {Object<string, Object>} comboFormData  Keyed by combinationNumber.
 * @param {Array<{combinationNumber: string}>} designCombinations
 * @returns {{ varyingKeys: Set<string>, commonKeys: Set<string>, headlineKey: string|null }}
 */
export function computeDifferentiator(combinationFields, comboFormData, designCombinations) {
  const varyingKeys = new Set();
  const commonKeys = new Set();

  // Single-variant designs can't have varying attrs by definition.
  const singleVariant = (designCombinations || []).length <= 1;

  (combinationFields || []).forEach(f => {
    const fk = f.fieldKey;
    const normalised = (designCombinations || []).map(c => {
      const raw = (comboFormData?.[c.combinationNumber] || {})[fk];
      return normaliseComboValue(raw, f.multiSelect);
    });
    const filled = normalised.filter(v => v !== '');
    const distinct = new Set(filled);

    let varying = false;
    if (!singleVariant) {
      if (distinct.size >= 2) varying = true;
      // Partial fill: at least 1 has value, at least 1 is empty.
      else if (filled.length > 0 && filled.length < normalised.length) varying = true;
    }
    if (varying) varyingKeys.add(fk);
    else commonKeys.add(fk);
  });

  // First varying key in config display order wins the headline slot.
  let headlineKey = null;
  for (const f of (combinationFields || [])) {
    if (varyingKeys.has(f.fieldKey)) { headlineKey = f.fieldKey; break; }
  }

  return { varyingKeys, commonKeys, headlineKey };
}

// Normalise a raw combo cell value to a comparable string. Empty → ''.
// Multi-select arrays / comma-strings are split, trimmed, sorted, and rejoined
// so that ['a','b'] and ['b','a'] compare equal.
function normaliseComboValue(raw, multiSelect) {
  if (raw === null || raw === undefined) return '';
  if (multiSelect) {
    let arr;
    if (Array.isArray(raw)) arr = raw;
    else if (typeof raw === 'string') arr = raw.split(',');
    else arr = [raw];
    const cleaned = arr.map(s => String(s).trim()).filter(Boolean).sort();
    return cleaned.length === 0 ? '' : cleaned.join('|');
  }
  const s = String(raw).trim();
  return s;
}

/**
 * Immutably overwrites a single field for every combination in the design.
 * Returns a brand-new comboFormData object (and brand-new per-combo objects
 * for every touched row) so that snapshot/undo can compare by reference.
 *
 * Used by the BulkFillBar in `CombinationTab.js` — the prior comboFormData
 * is captured as a snapshot before this call, so the UndoToast can restore
 * it via setComboFormData(snapshot) within the 5-second window.
 *
 * Per-combo objects are deep-spread only for rows whose combinationNumber
 * appears in `designCombinations`. Rows present in comboFormData but absent
 * from designCombinations are aliased into the returned object — fine for
 * snapshot/undo (which restores the whole map by reference) but callers
 * must not mutate them in place.
 *
 * @param {Object<string, Object>} comboFormData
 * @param {Array<{combinationNumber: string}>} designCombinations
 * @param {string} fieldKey
 * @param {string|number|string[]} value
 * @returns {Object<string, Object>}
 */
export function applyBulkFill(comboFormData, designCombinations, fieldKey, value) {
  const next = { ...(comboFormData || {}) };
  (designCombinations || []).forEach(c => {
    const cn = c.combinationNumber;
    next[cn] = { ...(next[cn] || {}), [fieldKey]: value };
  });
  return next;
}
